/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {IModuleProps, INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ISignUpProps } from '../definition-extensions/sign-up.ext.props.autogenerated';

export interface ISignUpInputProps {
    signUpLocalAccount:ISignUpLocalAccount;
}
/*
* Added ISignUpViewState, ISignUpViewProps, ISignUpEmailVerification, ISignUpLocalAccount, ISignUpLoading, ISignUpItem
* keys overwritten error is coming if refer to the modules type because of the known issue in the new version of Typescript
*/
export interface ISignUpViewState {
    isShowLoading: boolean;
}
export interface ISignUpEmailVerification {
    isRequired: boolean;
    email: ISignUpItem;
    buttonWrapper: INodeProps;
    buttons: React.ReactNode[];
    successMessage: React.ReactNode[];
    errorMessage: React.ReactNode[];
}
export interface ISignUpViewProps {
    className: string;
    viewState: ISignUpViewState;
    loading: ISignUpLoading;
    signUp: IModuleProps;
    defaultAADConainer: INodeProps;
    aadConainer: INodeProps;
    heading: React.ReactNode;
    signUpLocalAccount: ISignUpLocalAccount;
}
export interface ISignUpLocalAccount {
    localAccount: INodeProps;
    items: ISignUpItem[];
    emailVerification: ISignUpEmailVerification;
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}
export interface ISignUpLoading {
    modal: INodeProps;
    modalBody: INodeProps;
    icon: React.ReactNode;
    message: React.ReactNode;
}
export interface ISignUpItem {
    wrapper: INodeProps;
    key: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input: React.ReactNode;
}
const SignUpItem: React.FC<ISignUpItem> = ({
    wrapper,
    label,
    errorMessage,
    input
}) => {

    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({
    email,
    buttonWrapper,
    buttons,
    successMessage,
    errorMessage,
}) => {
    return (
        <>
            <SignUpItem {...email} />
            {successMessage && successMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            {errorMessage && errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            <Node {...buttonWrapper}>
                {buttons && buttons.map((button: React.ReactNode) => {
                    return (
                        <>
                            {button}
                        </>
                    );
                })}
            </Node>
        </>
    );
};

const LocalAccount: React.FC<ISignUpInputProps> = ({
    signUpLocalAccount
}) => {
    const{
        localAccount,
        items,
        emailVerification,
        buttons,
        errorMessage,
        disclaimer
    } = signUpLocalAccount;

    

    return (
        <Node {...localAccount}>
            {items && items.map((item: ISignUpItem) => {
                if (item.key === 'email' && emailVerification.isRequired) {
                    return (
                        <>
                            <SignUpItem  {...item} />
                            <SignUpEmailVerification {...emailVerification} />
                        </>
                    );
                }
                return (
                    <>
                    <SignUpItem  {...item} />
                    </>
                );
            })}
            {errorMessage && errorMessage.map((error: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {error}
                    </React.Fragment>
                );
            })}
            {buttons && buttons.map((button: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {button}
                    </React.Fragment>
                );
            })}
            {disclaimer}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({
    modal,
    modalBody,
    icon,
    message,
}) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps & ISignUpProps<{}>> = props => {

    const {
        signUp,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        heading,
        signUpLocalAccount,
    } = props;
    const signUpInput:ISignUpInputProps = {
        signUpLocalAccount
    };
   
    
    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpInput)}
                </Node>
        </Module>
    );
};

export default SignUpView;
